import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Tag, BarChart3 } from "lucide-react";
import { format } from "date-fns";
import { CollaboratorPromotion } from "@/types/collaborator";

interface CollaboratorStats {
  totalEarnings: number;
  pendingEarnings: number;
  activePromotions: number;
  totalSubscriptions: number;
}

interface CollaboratorSummaryProps {
  user: {
    id: number;
    name: string;
  };
  collaboratorData: CollaboratorPromotion[] | null;
  collaboratorStats: CollaboratorStats;
  isLoadingCollaboratorData: boolean;
}

const CollaboratorSummary: React.FC<CollaboratorSummaryProps> = ({
  user,
  collaboratorData,
  collaboratorStats,
  isLoadingCollaboratorData,
}) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Tag className="h-5 w-5" />
          Collaborator Program
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoadingCollaboratorData ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !collaboratorData || collaboratorData.length === 0 ? (
          <div className="text-center py-2">
            <Badge
              variant="outline"
              className="bg-muted/50 text-muted-foreground"
            >
              No collaborations yet
            </Badge>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-lg font-bold">{collaboratorData.length}</p>
                <p className="text-xs text-muted-foreground">Collaborations</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold">
                  ₹{collaboratorStats.totalEarnings.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">Total Earnings</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last Activity</span>
                <span className="font-medium">
                  {collaboratorData.length > 0
                    ? format(
                        new Date(
                          Math.max(
                            ...collaboratorData.map((p) =>
                              new Date(p.created_at).getTime()
                            )
                          )
                        ),
                        "MMM dd, yyyy"
                      )
                    : "Never"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge
                  variant={
                    collaboratorStats.activePromotions > 0
                      ? "default"
                      : "outline"
                  }
                  className={
                    collaboratorStats.activePromotions > 0 ? "bg-green-500" : ""
                  }
                >
                  {collaboratorStats.activePromotions > 0
                    ? "Active"
                    : "Inactive"}
                </Badge>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={() => navigate(`/admin/collaborator/${user.id}`)}
            >
              <BarChart3 className="mr-2 h-4 w-4" />
              Collaborator Dashboard
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CollaboratorSummary;
